import React, { useState, useEffect, useCallback } from "react";
import {
  Users, Phone, Mail, Calendar, Target,
  Loader2, Clock, ChevronDown, ChevronUp, Send
} from "lucide-react";
import { logger } from "../utils/logger";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SalesExecDashboard = ({ auth, authAxios }) => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("all");
  const [expandedLead, setExpandedLead] = useState(null);
  const [followUpNote, setFollowUpNote] = useState("");
  const [followUpType, setFollowUpType] = useState("note");

  const fetchMyLeads = useCallback(async () => {
    try {
      const res = await authAxios.get(`${API_URL}/api/admin/sales/my-leads`);
      setLeads(res.data);
    } catch (err) { logger.error(err); }
    setLoading(false);
  }, [authAxios]);

  useEffect(() => { fetchMyLeads(); }, [fetchMyLeads]);

  const updateStatus = async (leadId, status) => {
    await authAxios.put(`${API_URL}/api/admin/leads/${leadId}/status`, { status });
    await fetchMyLeads();
  };

  const addFollowUp = async (leadId) => {
    if (!followUpNote.trim()) return;
    await authAxios.post(`${API_URL}/api/admin/leads/${leadId}/follow-up`, { note: followUpNote, type: followUpType });
    setFollowUpNote("");
    await fetchMyLeads();
  };

  const filtered = leads.filter(l => filterStatus === "all" || (l.status || "new") === filterStatus);

  const statusColors = {
    new: "bg-blue-100 text-blue-700", assigned: "bg-indigo-100 text-indigo-700",
    contacted: "bg-yellow-100 text-yellow-700", qualified: "bg-purple-100 text-purple-700",
    converted: "bg-green-100 text-green-700", lost: "bg-red-100 text-red-700",
  };

  const stats = {
    total: leads.length,
    new: leads.filter(l => (l.status || "new") === "new" || l.status === "assigned").length,
    contacted: leads.filter(l => l.status === "contacted" || l.status === "qualified").length,
    converted: leads.filter(l => l.status === "converted").length,
  };

  if (loading) return <div className="text-center py-12"><Loader2 className="animate-spin mx-auto text-[#E83E8C]" size={32} /></div>;

  return (
    <div className="space-y-6" data-testid="sales-exec-dashboard">
      <div className="flex items-center gap-3 mb-2">
        <Target size={24} className="text-[#E83E8C]" />
        <h2 className="text-xl font-bold text-[#1E1B4B] font-['Outfit']">My Leads</h2>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-white rounded-2xl shadow-lg p-5 text-center">
          <p className="text-3xl font-bold text-[#E83E8C]">{stats.total}</p>
          <p className="text-sm text-[#475569]">Assigned</p>
        </div>
        <div className="bg-white rounded-2xl shadow-lg p-5 text-center">
          <p className="text-3xl font-bold text-blue-500">{stats.new}</p>
          <p className="text-sm text-[#475569]">To Contact</p>
        </div>
        <div className="bg-white rounded-2xl shadow-lg p-5 text-center">
          <p className="text-3xl font-bold text-[#FF9F1C]">{stats.contacted}</p>
          <p className="text-sm text-[#475569]">In Progress</p>
        </div>
        <div className="bg-white rounded-2xl shadow-lg p-5 text-center">
          <p className="text-3xl font-bold text-[#25D366]">{stats.converted}</p>
          <p className="text-sm text-[#475569]">Converted</p>
        </div>
      </div>

      {/* Filter */}
      <div className="flex gap-2">
        {["all", "new", "assigned", "contacted", "qualified", "converted"].map(s => (
          <button key={s} onClick={() => setFilterStatus(s)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all capitalize ${filterStatus === s ? 'bg-[#E83E8C] text-white' : 'bg-white text-[#475569] hover:bg-[#FFFAF5]'}`}>
            {s === "all" ? "All" : s}
          </button>
        ))}
      </div>

      {/* Leads */}
      <div className="bg-white rounded-3xl shadow-lg p-6">
        {filtered.length === 0 ? (
          <p className="text-center text-[#94a3b8] py-8">No leads found. Leads assigned to you will appear here.</p>
        ) : (
          <div className="space-y-2">
            {filtered.map((l, i) => (
              <div key={l.id || i} className="rounded-xl border border-gray-100 hover:border-[#E83E8C]/30 transition-all">
                <div className="flex items-center gap-3 p-3 cursor-pointer" onClick={() => setExpandedLead(expandedLead === l.id ? null : l.id)}>
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#E83E8C] to-[#FF9F1C] flex items-center justify-center text-white font-bold text-sm">
                    {(l.name || "?")[0].toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm text-[#1E1B4B]">{l.name || "Unknown"}</p>
                    <p className="text-xs text-[#94a3b8] flex items-center gap-2">
                      <Phone size={10} /> {l.phone || "No phone"}
                      {l.occasion && <span className="ml-2">{l.occasion}</span>}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {l.preferred_package && <span className="px-2 py-0.5 bg-[#FF9F1C]/10 text-[#FF9F1C] rounded-full text-xs hidden sm:inline">{l.preferred_package}</span>}
                    {l.wa_msg_count > 0 && <span className="px-2 py-0.5 bg-[#25D366]/10 text-[#25D366] rounded-full text-xs">{l.wa_msg_count} msgs</span>}
                    <select value={l.status || "new"} onChange={e => { e.stopPropagation(); updateStatus(l.id, e.target.value); }}
                      className={`px-2 py-1 rounded-lg text-xs font-medium border-0 outline-none cursor-pointer ${statusColors[l.status || "new"]}`}
                      onClick={e => e.stopPropagation()}>
                      <option value="new">New</option>
                      <option value="contacted">Contacted</option>
                      <option value="qualified">Qualified</option>
                      <option value="converted">Converted</option>
                      <option value="lost">Lost</option>
                    </select>
                    {expandedLead === l.id ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  </div>
                </div>

                {expandedLead === l.id && (
                  <div className="px-4 pb-4 border-t border-gray-100 space-y-3 pt-3">
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      {l.email && <div><span className="text-[#94a3b8] text-xs">Email</span><p className="text-[#1E1B4B] flex items-center gap-1"><Mail size={12} /> {l.email}</p></div>}
                      {l.preferred_date && <div><span className="text-[#94a3b8] text-xs">Preferred Date</span><p className="text-[#1E1B4B] flex items-center gap-1"><Calendar size={12} /> {l.preferred_date}</p></div>}
                      {l.notes && <div className="col-span-2"><span className="text-[#94a3b8] text-xs">Notes</span><p className="text-[#1E1B4B]">{l.notes}</p></div>}
                    </div>

                    {/* Follow-ups */}
                    {(l.follow_ups || []).length > 0 && (
                      <div className="space-y-1.5 max-h-32 overflow-y-auto">
                        {l.follow_ups.map((f, j) => (
                          <div key={f.at || `fu-${j}`} className="flex items-start gap-2 text-xs p-2 bg-gray-50 rounded-lg">
                            <span className={`px-1.5 py-0.5 rounded ${f.type === 'call' ? 'bg-blue-100 text-blue-600' : f.type === 'whatsapp' ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-600'}`}>{f.type}</span>
                            <span className="flex-1">{f.note}</span>
                            <span className="text-[#94a3b8]">{f.at?.split("T")[0]}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Add Follow-up */}
                    <div className="flex gap-2 items-center">
                      <select value={followUpType} onChange={e => setFollowUpType(e.target.value)}
                        className="px-2 py-1.5 rounded-lg border text-xs outline-none">
                        <option value="note">Note</option>
                        <option value="call">Call</option>
                        <option value="whatsapp">WhatsApp</option>
                      </select>
                      <input value={followUpNote} onChange={e => setFollowUpNote(e.target.value)} placeholder="Add follow-up..."
                        className="flex-1 px-3 py-1.5 rounded-lg border text-sm focus:border-[#E83E8C] outline-none"
                        onKeyDown={e => e.key === 'Enter' && addFollowUp(l.id)} />
                      <button onClick={() => addFollowUp(l.id)} className="px-3 py-1.5 bg-[#E83E8C] text-white rounded-lg text-xs font-medium">Add</button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SalesExecDashboard;
